// Full-dataset parcel search index for the vegetation change demo.
//
// The on-map search can only see parcels in currently-rendered tiles
// (`queryRenderedFeatures` / `querySourceFeatures` are viewport-bound),
// so a parcel that's off-screen or dropped by tippecanoe at low zoom is
// unfindable. This sidecar lists EVERY parcel in the served
// cadastre.pmtiles with one representative coordinate, so the search box
// can match across the whole dataset and then fly to the result.
//
// Built from the served archive (so it stays in lockstep with the live
// tiles) — see scripts/demo-nab-deforestation/build-parcel-search-index.py.

import { PARCEL_SEARCH_INDEX_URL } from "../config/deforestation";

export interface ParcelIndexEntry {
  cadastrePid: string;
  parcelId: string;
  lot: string | null;
  plan: string | null;
  state: string;
  lng: number;
  lat: number;
}

let index: ParcelIndexEntry[] | null = null;
let indexPromise: Promise<ParcelIndexEntry[]> | null = null;

export function ensureParcelSearchIndex(): Promise<ParcelIndexEntry[]> {
  if (index) return Promise.resolve(index);
  if (indexPromise) return indexPromise;
  indexPromise = fetch(PARCEL_SEARCH_INDEX_URL)
    .then((res) => {
      if (!res.ok) {
        throw new Error(
          `parcel-search-index fetch failed: ${res.status} ${res.statusText}`,
        );
      }
      return res.json();
    })
    .then((data: ParcelIndexEntry[]) => {
      index = data;
      return data;
    })
    .catch((err) => {
      console.error("[vegetation-change] search index load failed:", err);
      index = [];
      return index;
    });
  return indexPromise;
}

// Synchronous query against the cached index — returns up to `limit`
// matches on parcelId / lot / plan / cadastrePid (case-insensitive
// substring). Returns [] until the index has loaded.
export function queryParcelIndex(
  query: string,
  limit = 25,
): ParcelIndexEntry[] {
  const q = query.trim().toLowerCase();
  if (!q || !index) return [];
  const hits: ParcelIndexEntry[] = [];
  for (const e of index) {
    const match =
      e.parcelId.toLowerCase().includes(q) ||
      e.cadastrePid.toLowerCase().includes(q) ||
      (e.lot ? e.lot.toLowerCase().includes(q) : false) ||
      (e.plan ? e.plan.toLowerCase().includes(q) : false);
    if (match) {
      hits.push(e);
      if (hits.length >= limit) break;
    }
  }
  return hits;
}
